import { Injectable } from '@angular/core';
import { FileEntry } from '@ionic-native/file';
import { Actions, Effect } from '@ngrx/effects';
import { Platform } from 'ionic-angular';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { of } from 'rxjs/observable/of';
import { VasAppFacade } from '../../services/vas-app-facade.service';
import * as actions from './file.actions';
import { FileHelper } from '../../../app/helpers/file-system/file.helper';
import { FileSystemHelper } from '../../../app/helpers/file-system.helper';
import { CordovaFileHelper } from '../../../app/helpers/file-system/cordova-file.helper';

@Injectable()
export class FileEffects {

    get isDesktop(): boolean {
        return this.platform.is('core');
    }

    @Effect()
    preview$ = this.actions$.ofType(actions.FILE_PREVIEW)
        .switchMap((action: actions.ViewFileAction) => {
            return this.isDesktop ?
                of(new actions.FilePreviewDesktopAction({
                    file: action.payload.file
                }) as any) :
                of(new actions.FilePreviewMobileAction({
                    file: action.payload.file
                }) as any);
        });

    @Effect()
    previewMobile$ = this.actions$.ofType(actions.FILE_PREVIEW_MOBILE)
        .switchMap((action: actions.FilePreviewMobileAction) => {
            return this.appFacade.objects.file.cordova
                .fileExists(action.payload.file)
                .map(exists => exists ?
                    new actions.FilePreviewMobileLocalAction(action.payload) :
                    new actions.FilePreviewMobileRemoteAction(action.payload)
                );
        });

    @Effect({dispatch: false})
    previewMobileLocal$ = this.actions$
        .ofType(actions.FILE_PREVIEW_MOBILE_LOCAL)
        .switchMap((action: actions.FilePreviewMobileLocalAction) => {
            return fromPromise(this.appFacade.objects.file.cordova
                .fileToFileEntry(
                    action.payload.file,
                    {
                        create: false
                    }
                ))
                .concatMap((fileEntry: FileEntry) =>
                    this.appFacade.objects.file.cordova.openFile(
                        fileEntry.toURL(),
                        action.payload.file.mimeType
                    )
                )
                .catch(error =>
                    this.appFacade.toast.toastDanger(error)
                );
        });

    @Effect({dispatch: false})
    previewMobileRemote$ = this.actions$
        .ofType(actions.FILE_PREVIEW_MOBILE_REMOTE)
        .switchMap((action: actions.FilePreviewMobileRemoteAction) => {
            return this.appFacade.loading
                .showLoading$('Downloading file')
                .concatMap(() =>
                    this.appFacade.objects.file.cordova.streamRemoteFileToDisk(
                        action.payload.file
                    )
                )
                .do(() => this.appFacade.loading.hideLoading())
                .concatMap(fileEntry =>
                    this.appFacade.objects.file.cordova.openFile(
                        fileEntry.toURL(),
                        action.payload.file.mimeType
                    )
                )
                .catch(error =>
                    this.appFacade.loading.hideLoading$()
                        .concatMap(() =>
                            this.appFacade.toast.toastDanger(error)
                        )
                );
        });

    @Effect({dispatch: false})
    previewDesktop$ = this.actions$.ofType(actions.FILE_PREVIEW_DESKTOP)
        .switchMap((action: actions.FilePreviewDesktopAction) => {
            return this.appFacade.loading
                .showLoading$('Downloading file')
                .concatMap(() => {

                    switch (true) {
                        case !!action.payload.file.imageDataUrl: {
                            return of(FileHelper.dataUrlToBlob(
                                action.payload.file.imageDataUrl
                            ));
                        }
                        case !!action.payload.file.localFileSystemPath: {

                            const parsed = FileHelper.parsePath(
                                action.payload.file.localFileSystemPath
                            );

                            if (parsed.cordova) {
                                return CordovaFileHelper.pathToBlob(
                                    action.payload.file.localFileSystemPath,
                                    this.appFacade.nativeFileService
                                );
                            }

                            return fromPromise(FileSystemHelper.pathToBlob(
                                action.payload.file.localFileSystemPath
                            ));
                        }
                        default: {
                            return this.appFacade.objects.file.api.getFileContent(
                                action.payload.file
                            );
                        }
                    }
                })
                .do(() => this.appFacade.loading.hideLoading$())
                .concatMap(blob =>
                    this.appFacade.objects.file.desktop.blobToPreviewPage(blob)
                )
                .catch(async error =>
                    this.appFacade.loading.hideLoading$()
                        .concatMap(() =>
                            this.appFacade.toast.toastDanger(error)
                        )
                );
        });

    constructor(
        protected actions$: Actions,
        protected platform: Platform,
        protected appFacade: VasAppFacade
    ) {

    }
}



// WEBPACK FOOTER //
// ./src/vas/business-objects/file/file.effects.ts